import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchData, updateData } from '../services/api';
import type { Notification, NotificationType } from '@shared/types';

export default function Notifications() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const { data: notifications, isLoading } = useQuery({
    queryKey: ['notifications', filter],
    queryFn: () => {
      const params = filter === 'unread' ? '?read=false' : '';
      return fetchData<Notification[]>(`/notifications${params}`);
    },
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => updateData(`/notifications/${id}`, { read: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => updateData('/notifications/mark-all-read', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const unreadCount = notifications?.filter((n) => !n.read).length || 0;

  return (
    <div className="space-y-4 pb-20 lg:pb-0">
      {/* Header */}
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Alerts</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllReadMutation.mutate()}
            className="btn-ghost text-sm"
            disabled={markAllReadMutation.isPending}
          >
            Mark all read
          </button>
        )}
      </header>

      {/* Filter */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-primary-500 text-white'
              : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
            filter === 'unread'
              ? 'bg-primary-500 text-white'
              : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
          }`}
        >
          Unread
          {unreadCount > 0 && (
            <span className={`px-1.5 py-0.5 rounded-full text-xs ${
              filter === 'unread' ? 'bg-white/20' : 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300'
            }`}>
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Notifications list */}
      <div className="space-y-2">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="card flex items-start gap-3">
              <div className="skeleton w-10 h-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-4 w-3/4" />
                <div className="skeleton h-3 w-full" />
                <div className="skeleton h-3 w-1/4" />
              </div>
            </div>
          ))
        ) : notifications && notifications.length > 0 ? (
          <>
            {/* Group by date */}
            {groupNotificationsByDate(notifications).map(([date, items]) => (
              <div key={date}>
                <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-2 mt-4 first:mt-0">
                  {date}
                </h3>
                {items.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                    onMarkRead={() => markReadMutation.mutate(notification.id)}
                  />
                ))}
              </div>
            ))}
          </>
        ) : (
          <EmptyNotifications filter={filter} />
        )}
      </div>
    </div>
  );
}

function NotificationCard({
  notification,
  onMarkRead,
}: {
  notification: Notification;
  onMarkRead: () => void;
}) {
  const typeConfig: Record<NotificationType, { icon: string; color: string }> = {
    child_event: { icon: '👶', color: 'warmth' },
    custody_reminder: { icon: '📅', color: 'warmth' },
    communication: { icon: '📧', color: 'primary' },
    finance_alert: { icon: '💰', color: 'green' },
    deadline: { icon: '⏰', color: 'red' },
    webhook: { icon: '🔔', color: 'purple' },
    system: { icon: '⚙️', color: 'neutral' },
  };

  const config = typeConfig[notification.type] || typeConfig.system;
  const isChildRelated = ['child_event', 'custody_reminder'].includes(notification.type);

  const priorityStyles = {
    urgent: 'priority-urgent',
    high: 'priority-high',
    normal: '',
    low: '',
  };

  return (
    <div
      className={`card-interactive mb-2 ${priorityStyles[notification.priority]} ${
        isChildRelated ? 'child-related' : ''
      } ${!notification.read ? 'bg-primary-50/50 dark:bg-primary-900/10' : ''}`}
      onClick={() => {
        if (!notification.read) onMarkRead();
        if (notification.actionUrl) {
          window.location.href = notification.actionUrl;
        }
      }}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg shrink-0 ${
          config.color === 'warmth' ? 'bg-warmth-100 dark:bg-warmth-900/30' :
          config.color === 'primary' ? 'bg-primary-100 dark:bg-primary-900/30' :
          config.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
          config.color === 'red' ? 'bg-red-100 dark:bg-red-900/30' :
          config.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30' :
          'bg-neutral-100 dark:bg-neutral-800'
        }`}>
          {config.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className={`font-medium ${!notification.read ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-700 dark:text-neutral-300'}`}>
              {notification.title}
            </h3>
            {!notification.read && (
              <div className="w-2 h-2 rounded-full bg-primary-500 shrink-0 mt-2" />
            )}
          </div>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1 readable-text">
            {notification.body}
          </p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs text-neutral-500 dark:text-neutral-500">
              {formatNotificationTime(notification.createdAt)}
            </span>
            {notification.source && (
              <span className="text-xs text-neutral-400 dark:text-neutral-600">
                via {notification.source}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Priority badge for urgent */}
      {notification.priority === 'urgent' && (
        <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-800">
          <span className="badge-urgent">Requires immediate attention</span>
        </div>
      )}
    </div>
  );
}

function EmptyNotifications({ filter }: { filter: string }) {
  return (
    <div className="text-center py-12">
      <span className="text-5xl mb-4 block">🔔</span>
      <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">
        {filter === 'unread' ? 'No unread notifications' : 'No notifications'}
      </h3>
      <p className="text-neutral-500 dark:text-neutral-400 readable-text">
        {filter === 'unread'
          ? "You're all caught up!"
          : 'Notifications from your connected apps will appear here'}
      </p>
    </div>
  );
}

function groupNotificationsByDate(notifications: Notification[]): [string, Notification[]][] {
  const groups: Record<string, Notification[]> = {};

  notifications.forEach((notification) => {
    const date = new Date(notification.createdAt);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let label: string;
    if (isSameDay(date, today)) {
      label = 'Today';
    } else if (isSameDay(date, yesterday)) {
      label = 'Yesterday';
    } else if (isThisWeek(date)) {
      label = 'This Week';
    } else {
      label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }

    if (!groups[label]) {
      groups[label] = [];
    }
    groups[label].push(notification);
  });

  return Object.entries(groups);
}

function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

function isThisWeek(date: Date): boolean {
  const now = new Date();
  const weekAgo = new Date(now);
  weekAgo.setDate(weekAgo.getDate() - 7);
  return date >= weekAgo && date <= now;
}

function formatNotificationTime(date: Date): string {
  const now = new Date();
  const d = new Date(date);
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;

  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
